// @ts-strict-ignore
import * as React from 'react';
import { Experiment, ExperimentClient } from '@amplitude/experiment-js-client';
import { AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY_DEV, AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY_PROD } from '../constants';
import * as Logger from './logger';
import * as amplitude from '@amplitude/experiment-js-client';
import { isDevelopmentEnvironment } from '@/helpers';
import { OrganizationEntity } from '@/types';

export enum FeatureFlag {
    OrgOverviewRevamp = 'org-overview-revamp',
    ReleaseCalendarRevamp = 'release-calendar-revamp',
    WFReleaseCalendar = 'wf-release-calendar',
    RolloutGroupSummary = 'rollout-group-summary',
}

export enum FeatureFlagVariantValue {
    On = 'on',
}

export const INTERNAL_FEATURE_FLAG_OVERRIDE_STORAGE_KEY = 'internal-feature-flag-overrides';
export const INTERNAL_FEATURE_FLAG_OVERRIDE_CHANGE_EVENT = 'internal-feature-flag-overrides-updated';
export const FEATURE_FLAG_FORCE_OFF_VALUE = 'off';

export type FeatureFlagOverrideValue = FeatureFlagVariantValue | typeof FEATURE_FLAG_FORCE_OFF_VALUE;
export type InternalFeatureFlagOverrides = Partial<Record<FeatureFlag, FeatureFlagOverrideValue>>;

export const initializeAmplitudeExperimentClient: (isDevEnv: boolean) => ExperimentClient = (isDevEnv) => {
    const experimentDeploymentKey =
        isDevEnv ? AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY_DEV : AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY_PROD;

    return Experiment.initializeWithAmplitudeAnalytics(experimentDeploymentKey);
};

export const getInternalFeatureFlagOverrides: () => InternalFeatureFlagOverrides = () => {
    return JSON.parse(window.localStorage.getItem(INTERNAL_FEATURE_FLAG_OVERRIDE_STORAGE_KEY) ?? 'null') ?? {};
};

const notifyInternalFeatureFlagOverridesUpdated = () => {
    window.dispatchEvent(new Event(INTERNAL_FEATURE_FLAG_OVERRIDE_CHANGE_EVENT));
};

export const setInternalFeatureFlagOverride: (flag: FeatureFlag, value?: FeatureFlagOverrideValue) => void = (
    flag,
    value,
) => {
    const overrides = getInternalFeatureFlagOverrides();

    if (value) {
        overrides[flag] = value;
    } else {
        delete overrides[flag];
    }

    window.localStorage.setItem(INTERNAL_FEATURE_FLAG_OVERRIDE_STORAGE_KEY, JSON.stringify(overrides));
    notifyInternalFeatureFlagOverridesUpdated();
};

const subscribeToInternalFeatureFlagOverrides = (onStoreChange: () => void) => {
    const handleStorageChange = (event: StorageEvent) => {
        if (!event.key || event.key === INTERNAL_FEATURE_FLAG_OVERRIDE_STORAGE_KEY) {
            onStoreChange();
        }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener(INTERNAL_FEATURE_FLAG_OVERRIDE_CHANGE_EVENT, onStoreChange);

    return () => {
        window.removeEventListener('storage', handleStorageChange);
        window.removeEventListener(INTERNAL_FEATURE_FLAG_OVERRIDE_CHANGE_EVENT, onStoreChange);
    };
};

export const useInternalFeatureFlagOverrides = (): InternalFeatureFlagOverrides => {
    const [overrides, setOverrides] = React.useState<InternalFeatureFlagOverrides>(() =>
        getInternalFeatureFlagOverrides(),
    );

    React.useEffect(() => {
        return subscribeToInternalFeatureFlagOverrides(() => {
            setOverrides(getInternalFeatureFlagOverrides());
        });
    }, []);

    return overrides;
};

export type ExperimentUser = amplitude.ExperimentUser;

export const startAmplitudeExperimentClient: (experimentUser: ExperimentUser, isDevEnv?: boolean) => void = (
    experimentUser,
    isDevEnv = isDevelopmentEnvironment(),
) => {
    const experimentClient = initializeAmplitudeExperimentClient(isDevEnv);

    experimentClient
        .start(experimentUser) //  used for immediate evaluation based on user props that can change
        .then(() => {
            if (isDevEnv) {
                Logger.log('Amplitude experiment successfully SDK started');
            }
        })
        .catch((error) => {
            if (isDevEnv) {
                Logger.log('Amplitude experiment failed to start with error: ' + error);
            }
        });
};

// Warning - ONLY call this *after* "startAmplitudeExperimentClient" has been called - ie. after the user has signed in
// Return value can be null, indicating the user hasn't been bucketed into a treatment and we should fall back to the control value
export const getValueForFlag: (flag: FeatureFlag, isDevEnv?: boolean) => string = (
    flag,
    isDevEnv = isDevelopmentEnvironment(),
) => {
    const overriddenValue = getInternalFeatureFlagOverrides()[flag];

    if (overriddenValue === FEATURE_FLAG_FORCE_OFF_VALUE) {
        return null;
    }

    if (overriddenValue) {
        return overriddenValue;
    }

    const experimentClient = initializeAmplitudeExperimentClient(isDevEnv);

    const value = experimentClient.variant(flag).value;

    if (isDevEnv) {
        Logger.log(`Feature flag ${flag} is ${value}`);
    }

    return value;
};

export const useFeatureFlagValue = (flag: FeatureFlag): string => {
    useInternalFeatureFlagOverrides();
    return getValueForFlag(flag);
};

// First check Amplitude feature flag for overrides. If false, fallback to org-level settings
export const useShouldShowNewOrgOverview = (org: OrganizationEntity | null | undefined) => {
    return (
        useFeatureFlagValue(FeatureFlag.OrgOverviewRevamp) === FeatureFlagVariantValue.On ||
        org?.shouldShowNewOrgOverview
    );
};

export const useShouldShowRolloutGroupSummary = () => {
    return useFeatureFlagValue(FeatureFlag.RolloutGroupSummary) === FeatureFlagVariantValue.On;
};

export const useShouldShowNewReleaseCalendar = () => {
    return useFeatureFlagValue(FeatureFlag.ReleaseCalendarRevamp) === FeatureFlagVariantValue.On;
};

// First check Amplitude feature flag for overrides. If false, fallback to org-level settings
export const useShouldUseWFReleaseCalendar = (org: OrganizationEntity | null | undefined) => {
    return (
        useFeatureFlagValue(FeatureFlag.WFReleaseCalendar) === FeatureFlagVariantValue.On ||
        org?.shouldUseWFReleaseCalendar
    );
};
